import {EventEmitter, inject, Injectable} from '@angular/core';
import { BaseComponent } from '../../components/base/base.component';
import {FileService, ProjectService, ServiceFactoryService} from "@wms/app/services";
import {PageService} from "@wms/services";
import {get, isObject, set} from "lodash-es";
import {Subscription} from "rxjs";

export interface PageMeta {
  name: string;
  type: string;
  params: Array<any>;
};

@Injectable({
  providedIn: 'root'
})
export class TreeService extends BaseComponent {
  private fileService: FileService
  private pageService: PageService;
  private serviceFactory: ServiceFactoryService;
  private activePath: string;

  public pages: Array<PageMeta>;
  public resourcesTree: any;
  public libTree: any;
  public projectResourcesTree: any;
  public imageTree: any;
  public audioTree: any;
  public videoTree: any;
  public connectorTree: any;
  public serviceResourcesTree: any;
  public pagesListUpdate: EventEmitter<any> = new EventEmitter<any>();
  override registerDestroyListener: Subscription = new Subscription();


  constructor() {
    super();
    this.fileService = inject(FileService);
    this.pageService = inject(PageService);
    this.serviceFactory = inject(ServiceFactoryService);
    this.updatePagesTree();
    this.registerDestroyListener.add(this.eventManager.UpdatePageTreeObservable().subscribe((data: any) => {
      let callBack = data && data.callBack;
      this.updatePagesTree(callBack)
    }));
    this.registerDestroyListener.add(this.eventManager.UpdateResourcesTreeObservable().subscribe((data: any) => {
      this.updateResourcesTree()
    }));
  }

  updatePagesTree(callBack?: Function) {
    (this.pageService.list() as Promise<Array<PageMeta>>).then((response: Array<PageMeta>) => {
      this.projectService.setPages(response);
      this.pagesListUpdate.emit(response);
      // this.pages = response;
      callBack && callBack();
    }, () => {
      console.log('error ... in fetching pages tree');
      // wmToaster.show('error', this.root.locale['MESSAGE_ERROR_TITLE'], this.root.locale['MESSAGE_ERROR_UPDATE_PROJECT_PAGES_DESC']);
    });
  };
  updateResourcesTree() {
    /*Send a request to get updated resources - if path is empty, it will fetch all
     resources under 'src/main/webapp' folder */
    this.fileService.readDir('').then((result) => {
      this.resourcesTree = this.transformResourceTreeObject('resources', result, false, this.activePath);
    });
  };
  updateLibTree(showSpinner?:boolean) {
    this.fileService.readDir('../lib/',showSpinner).then((result) => {
      this.libTree = this.transformResourceTreeObject('lib', result, false, this.activePath);
    });
  };
  updateServiceResourcesTree() {
    /*Services folder will be available only if the project contains any services.
    * Hence check if services are present first.*/
    this.serviceFactory.getServicesWithType((response: any) => {
      if (response.length) {
        /*Send a request to get updated service resources.*/
        this.fileService.readDir('../services/').then((result) => {
          this.serviceResourcesTree = this.transformResourceTreeObject('services', result, false, this.activePath);
        });
      } else {
        this.serviceResourcesTree = this.transformResourceTreeObject('services', [], false, this.activePath);
      }
    });
  };
  updateProjectResourcesTree(nodePath?: string ,showSpinner?:boolean) {
    /*Send a request to get all resources in the lib folder of the project.*/
    this.fileService.readDir('../project/',showSpinner).then((result: any) => {
      /* setting root directory name as project name */
      if (isObject(result)) {
        set(result, 'name', get(result, 'name', this.projectService.getDetails().name));
      }
      this.projectResourcesTree = this.transformResourceTreeObject('project', result, false, nodePath || this.activePath);
    });
  };
  updateImageTree() {
    /*Send a request to get all resources in the images folder of the project.*/
    this.fileService.readDir('resources/images').then((result) => {
      this.imageTree = this.transformResourceTreeObject('image', result, false, this.activePath);
    });
  };
  updateAudioTree() {
    /*Send a request to get all resources in the audios folder of the project.*/
    this.fileService.readDir('resources/audios').then((result) => {
      this.audioTree = this.transformResourceTreeObject('audio', result, false, this.activePath);
    });
  };
  updateVideoTree() {
    /*Send a request to get all resources in the videos folder of the project.*/
    this.fileService.readDir( 'resources/videos').then((result) => {
      this.videoTree = this.transformResourceTreeObject('video', result, false, this.activePath);
    });
  };
  updateConnectorTree() {
    this.fileService.readDir('WEB-INF/connectors').then((result) => {
      this.connectorTree = this.transformResourceTreeObject('connector', result, false, this.activePath);
    });
  };
  updateResourceTree(resource: string) {
    switch (resource) {
      case 'lib':
      case 'jar':
        this.libTree = null;
        this.updateLibTree();
        break;
      case 'resources':
        this.resourcesTree = null;
        this.updateResourcesTree();
        break;
      case 'services':
        this.serviceResourcesTree = null;
        this.updateServiceResourcesTree();
        break;
      case 'project':
        this.projectResourcesTree = null;
        this.updateProjectResourcesTree();
        break;
      case 'image':
        this.imageTree = null;
        this.updateImageTree();
        break;
      case 'audio':
        this.audioTree = null;
        this.updateAudioTree();
        break;
      case 'video':
        this.videoTree = null;
        this.updateVideoTree();
        break;
      case 'connector':
        this.connectorTree = null;
        this.updateConnectorTree();
        break;
      default:
        break;
    }
  };

  private getAbsResourcePath(resourcesView: string, resourcePath: string) {
    let absResourcePath;
    switch (resourcesView) {
      case 'jar':
        absResourcePath = '../lib' + resourcePath;
        break;
      case 'services':
        absResourcePath = '../services' + resourcePath;
        break;
      case 'project':
        absResourcePath = '../project' + resourcePath;
        break;
      default:
        absResourcePath = resourcePath;
        break;
    }
    return absResourcePath;
  }

  /**
   * Transforms the project resource data received from backend API into the format the wms-tree understands
   * @param resourcesView
   * @param resourceObject
   * @param collapseNode
   * @param activePath
   */
  transformResourceTreeObject(resourcesView: string, resourceObject: any, collapseNode: boolean, activePath: string) : any {
    let resourceTreeObject:any[] = [],
      resourceFiles: any[] = [],
      folderClass: string = 'wms-folder',
      path: string,
      isActive: boolean;

    if (!resourceObject) {
      return;
    }

    /* Iterating recursively over resource object to get a tree */
    switch (resourceObject.type) {
      case 'folder':
        if (resourceObject.files.length > 0) {
          resourceObject.files.forEach((fileResource: any) => {
            path = this.getAbsResourcePath(resourcesView, fileResource.path);

            //TODO: If activePath is not available, set the resource path in activeWS.details as activePath.
            // if (!activePath && $rootScope.activeWS && $rootScope.activeWS.details) {
            //   activePath = $rootScope.activeWS.details.path;
            // }
            isActive = activePath ? (path ===  activePath) : false;

            if (fileResource.type === 'file') {
              resourceFiles.push({
                'id': fileResource.name,
                'class': 'wms-file',
                'props': {
                  'name': fileResource.name,
                  'type': fileResource.type,
                  'path': path,
                  'relativePath': fileResource.path
                },
                'active': isActive
              });
            } else if (fileResource.type === 'folder') {
              resourceFiles.push(this.transformResourceTreeObject(resourcesView, fileResource, true, activePath)[0]);
            }
          });
        }
        path = this.getAbsResourcePath(resourcesView, resourceObject.path);
        isActive = activePath ? (path === activePath) : false;

        if (activePath && activePath.indexOf(path) > -1) {
          collapseNode = false;
        }

        resourceTreeObject.push({
          'id': resourceObject.name,
          'class': folderClass,
          'children': resourceFiles,
          'props': {
            'name': resourceObject.name,
            'type': resourceObject.type,
            'path': path,
            'relativePath': resourceObject.path
          },
          'collapsed': collapseNode,
          'active': isActive
        });
        break;
      case 'file':
        resourceTreeObject.push({
          'id': resourceObject.name,
          'class': 'wms-file',
          'props': {
            'name': resourceObject.name,
            'type': resourceObject.type,
            'path': this.getAbsResourcePath(resourcesView, resourceObject.path),
            'relativePath': resourceObject.path
          }
        });
        break;
    }
    return resourceTreeObject;
  };
}
